import React, { useState, useMemo, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Menu, Button, App, Tooltip, Spin } from 'antd';
import {
    HomeOutlined,
    SearchOutlined,
    WalletOutlined,
    FileTextOutlined,
    FileSearchOutlined,
    LogoutOutlined,
    StopOutlined,
    UserOutlined,
    AppstoreOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { getAuth, hasSettlementAccount, isMustChangePassword, logout, SETTLEMENT_RESTRICTED_PATHS } from 'utils/auth';
import { usePermission } from 'components/PermissionProvider';
import { MenuTreeNode } from 'graphql/types';
import { findFirstLeafPath } from 'utils/menuTree';
import LanguageSwitcher from './LanguageSwitcher';

const ICON_MAP: Record<string, React.ReactNode> = {
    HomeOutlined: <HomeOutlined />,
    SearchOutlined: <SearchOutlined />,
    WalletOutlined: <WalletOutlined />,
    FileTextOutlined: <FileTextOutlined />,
    FileSearchOutlined: <FileSearchOutlined />,
    StopOutlined: <StopOutlined />,
    AppstoreOutlined: <AppstoreOutlined />,
};

// 后端 permissionName 是固定中文，i18n 由前端按 permissionCode 查表覆盖；
// 未命中 fallback 到 node.permissionName，避免暴露 i18n key 字面量。
const PERMISSION_NAME_I18N: Record<string, string> = {
    'portal:client':        'layout.nav.client',
    'portal:account-query': 'layout.nav.accountQuery',
    /*
     * ⚠️ 历史遗留：code 与展示名故意不对应 —— code 叫 order-batch-query，菜单显示的是「订单中心」。
     *
     * 由来：订单查询需求上线时，这个一级菜单从「订单批量查询」更名为「订单中心」，
     * 下面挂 订单查询（portal:order-query）+ 订单取消（portal:order-cancel）两个子菜单。
     * 更名只改了 DB 的 permission_name，permission_code 保留 portal:order-batch-query 不变。
     *
     * 为什么不一起改 code：permission_code 是 DB 与前后端硬编码常量之间的契约，
     * 改名要求「权限表 SQL + 后端发版 + 前端发版」同批次生效，而三者是独立动作、实例滚动更新，
     * 现实中必然存在新旧共存的时间窗；换来的只是命名整洁，不值当。
     * 因此本项目对 permission_code 一律按「只增不改」处理：新增 code 安全（先发版、后执行权限 SQL），
     */
    'portal:order-batch-query': 'layout.nav.orderCenter',
    'portal:balance':       'layout.nav.balance',
    'portal:usage-records': 'layout.nav.usageRecords',
    'portal:order-cancel':  'layout.nav.orderCancel',
    'portal:bill-management': 'layout.nav.billManagement',
    'portal:bill-list': 'layout.nav.billList',
    'portal:weighing-query': 'layout.nav.weighingQuery',
    'portal:order-query': 'layout.nav.orderQuery',
};

const getIcon = (iconName: string | null): React.ReactNode => {
    if (!iconName) return <AppstoreOutlined />;
    return ICON_MAP[iconName] || <AppstoreOutlined />;
};

const getMenuLabel = (node: MenuTreeNode, t: (key: string) => string): string => {
    const key = PERMISSION_NAME_I18N[node.permissionCode];
    return key ? t(key) : node.permissionName;
};

type MenuItem = Required<MenuProps>['items'][number];

const buildMenuItems = (nodes: MenuTreeNode[], t: (key: string) => string): MenuItem[] => {
    return nodes.map((node) => {
        const key = node.resourcePath || node.permissionCode;
        if (node.children && node.children.length > 0) {
            return {
                key,
                label: getMenuLabel(node, t),
                icon: getIcon(node.icon),
                children: buildMenuItems(node.children, t),
            };
        }
        return {
            key,
            label: getMenuLabel(node, t),
            icon: getIcon(node.icon),
        };
    });
};

const collectOpenKeys = (nodes: MenuTreeNode[]): string[] => {
    const keys: string[] = [];
    for (const node of nodes) {
        if (node.children && node.children.length > 0) {
            keys.push(node.resourcePath || node.permissionCode);
            keys.push(...collectOpenKeys(node.children));
        }
    }
    return keys;
};

// findFirstLeafPath 已抽到 utils/menuTree，与 AuthGuard 共用同一套「以菜单树为准」的口径
export { findFirstLeafPath };

const PortalLayout: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const auth = getAuth();
    const mustChange = isMustChangePassword();
    const { modal } = App.useApp();

    // 从 PermissionProvider Context 读取菜单树（与权限码共用一次 GraphQL 请求）
    const { menuTree: rawMenuTree, loading } = usePermission();

    // RBAC 之外的入口限制：未开通结算账户时，隐藏"余额 / 使用记录"等入口
    const menuTree = useMemo(() => {
        if (hasSettlementAccount()) return rawMenuTree;
        const filter = (nodes: MenuTreeNode[]): MenuTreeNode[] =>
            nodes
                .filter((n) => !n.resourcePath || !SETTLEMENT_RESTRICTED_PATHS.includes(n.resourcePath))
                .map((n) => ({ ...n, children: n.children ? filter(n.children) : [] }))
                .filter((n) => n.resourcePath || (n.children && n.children.length > 0));
        return filter(rawMenuTree);
    }, [rawMenuTree]);

    // 第一层节点作为左侧 tab
    const [activeTab, setActiveTab] = useState<string | null>(null);
    // 受控的展开 keys
    const [openKeys, setOpenKeys] = useState<string[]>([]);

    const activeTabKey = activeTab || (menuTree.length > 0 ? (menuTree[0].resourcePath || menuTree[0].permissionCode) : null);
    const activeTabNode = menuTree.find((node) => (node.resourcePath || node.permissionCode) === activeTabKey);

    const subMenuItems = useMemo(() => {
        if (!activeTabNode || !activeTabNode.children) return [];
        return buildMenuItems(activeTabNode.children, t);
    }, [activeTabNode, t]);

    // 当 activeTabNode 变化时，自动展开所有子菜单
    useEffect(() => {
        if (activeTabNode?.children) {
            setOpenKeys(collectOpenKeys(activeTabNode.children));
        }
    }, [activeTabNode]);

    // 菜单加载完成后，如果当前在根路径，自动跳转到第一个有权限的页面
    useEffect(() => {
        if (!loading && menuTree.length > 0 && location.pathname === '/') {
            const firstPath = findFirstLeafPath(menuTree);
            if (firstPath) {
                navigate(firstPath, { replace: true });
            }
        }
    }, [loading, menuTree, location.pathname, navigate]);

    const handleLogout = () => {
        modal.confirm({
            title: t('layout.logout'),
            content: t('layout.logoutConfirm'),
            onOk: () => logout(),
        });
    };

    const handleMenuClick = (e: { key: string }) => {
        if (e.key.startsWith('/')) {
            navigate(e.key);
        }
    };

    return (
        <div className="flex h-screen">
            {/* Left tab bar */}
            {/* 宽度自适应：随语言（中文短 / 英文长）在 72~132px 之间伸缩，避免英文菜单名被裁切 */}
            <div className="flex flex-col items-center w-fit min-w-[72px] max-w-[132px] bg-purple-50 border-r border-gray-200">
                {/* Logo */}
                <div className="flex items-center justify-center h-14 w-full border-b border-gray-200">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                        S
                    </div>
                </div>

                {/* Tab icons */}
                {!mustChange && (
                    <div className="flex flex-col items-center gap-1 pt-3 px-1.5 flex-1 w-full">
                        {loading ? (
                            <Spin size="small" className="mt-4" />
                        ) : (
                            menuTree.map((node) => {
                                const key = node.resourcePath || node.permissionCode;
                                return (
                                    <Tooltip key={key} title={getMenuLabel(node, t)} placement="right">
                                        <div
                                            className={`flex flex-col items-center justify-center w-full min-h-[56px] px-1 py-1.5 rounded-lg cursor-pointer transition-colors ${
                                                activeTabKey === key
                                                    ? 'bg-purple-100 text-purple-700'
                                                    : 'text-gray-500 hover:bg-gray-100'
                                            }`}
                                            onClick={() => setActiveTab(key)}
                                        >
                                            <span className="text-lg">{getIcon(node.icon)}</span>
                                            {/* 不再 nowrap：超出 max-w 时换行显示完整菜单名，配合外层 w-fit 实现自适应 */}
                                            <span className="text-[10px] mt-0.5 leading-tight text-center break-words">
                                                {getMenuLabel(node, t)}
                                            </span>
                                        </div>
                                    </Tooltip>
                                );
                            })
                        )}
                    </div>
                )}
            </div>

            {/* Right nav panel */}
            {/* 同样自适应：中文维持 180px，英文子菜单名更长时最多撑到 280px */}
            {!mustChange && activeTabNode && subMenuItems.length > 0 && (
                <div className="flex flex-col w-fit min-w-[180px] max-w-[280px] bg-white border-r border-gray-200">
                    {/* Nav header */}
                    <div className="flex items-center h-14 px-4 border-b border-gray-200">
                        <span className="font-semibold text-base text-gray-800 whitespace-nowrap">SwiftX</span>
                    </div>

                    {/* Sub menu */}
                    <div className="flex-1 overflow-y-auto">
                        <Menu
                            mode="inline"
                            selectedKeys={[location.pathname]}
                            openKeys={openKeys}
                            onOpenChange={setOpenKeys}
                            items={subMenuItems}
                            onClick={handleMenuClick}
                            style={{ borderRight: 'none' }}
                        />
                    </div>
                </div>
            )}

            {/* Main content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-end h-14 px-6 border-b border-gray-200 bg-white gap-3">
                    <LanguageSwitcher />
                    <div className="flex items-center gap-1 text-gray-600">
                        <UserOutlined />
                        <span className="text-sm">{auth?.accountNumber}</span>
                    </div>
                    <Button
                        type="text"
                        icon={<LogoutOutlined />}
                        onClick={handleLogout}
                        className="text-gray-500"
                    >
                        {t('layout.logout')}
                    </Button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto bg-gray-50 p-6">
                    <Outlet />
                </div>
            </div>
        </div>
    );
};

export default PortalLayout;
